import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useAccountContextStore } from '@/stores/accountContext'
import { usePlatformStore } from '@/stores/platform'

const routes: RouteRecordRaw[] = [
  // Public routes (no auth required)
  {
    path: '/',
    name: 'landing',
    component: () => import('@/views/public/LandingPageView.vue'),
    meta: { public: true },
  },
  {
    path: '/pricing',
    name: 'public-pricing',
    component: () => import('@/views/public/PricingPageView.vue'),
    meta: { public: true },
  },
  {
    path: '/faq',
    name: 'faq',
    component: () => import('@/views/public/FaqView.vue'),
    meta: { public: true },
  },
  {
    path: '/faq/:slug',
    name: 'faq-article',
    component: () => import('@/views/public/FaqArticleView.vue'),
    meta: { public: true },
    props: true,
  },
  {
    path: '/legal/:type',
    name: 'legal',
    component: () => import('@/views/public/LegalView.vue'),
    meta: { public: true },
    props: true,
  },

  {
    path: '/maintenance',
    name: 'maintenance',
    component: () => import('@/views/MaintenanceView.vue'),
    meta: { public: true },
  },

  // Guest routes (redirect to dashboard if authenticated)
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: { guest: true },
  },
  {
    path: '/register',
    name: 'register',
    component: () => import('@/views/RegisterView.vue'),
    meta: { guest: true },
  },
  {
    path: '/2fa',
    name: 'two-factor',
    component: () => import('@/views/TwoFactorChallengeView.vue'),
    meta: { guest: true },
  },
  {
    path: '/step-up',
    name: 'step-up',
    component: () => import('@/views/StepUpChallengeView.vue'),
    meta: { guest: true },
  },
  {
    path: '/verify-email/:token?',
    name: 'verify-email',
    component: () => import('@/views/EmailVerifyView.vue'),
    meta: { public: true },
  },
  {
    path: '/verify-domain/:token?',
    name: 'verify-domain',
    component: () => import('@/views/domains/VerifyDomainView.vue'),
    meta: { public: true },
  },
  {
    path: '/verify-ip/:token?',
    name: 'verify-ip',
    component: () => import('@/views/blacklist/VerifyMonitoredIpView.vue'),
    meta: { public: true },
  },
  {
    path: '/oauth/callback',
    name: 'oauth-callback',
    component: () => import('@/views/OAuthCallbackView.vue'),
    meta: { public: true },
  },
  {
    path: '/invite/accept',
    name: 'invite-accept',
    component: () => import('@/views/InviteAcceptView.vue'),
    meta: { public: true },
  },

  // Authenticated routes
  {
    path: '/select-account',
    name: 'select-account',
    component: () => import('@/views/AccountPickerView.vue'),
    meta: { requiresAuth: true, accountPicker: true },
  },
  {
    path: '/app',
    component: () => import('@/components/layout/AppShell.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'dashboard',
        component: () => import('@/views/domains/DashboardView.vue'),
      },
      {
        path: 'domains',
        name: 'domains',
        component: () => import('@/views/domains/DomainListView.vue'),
      },
      {
        path: 'domains/add',
        name: 'domain-add',
        component: () => import('@/views/domains/DomainAddView.vue'),
      },
      {
        path: 'domains/import',
        name: 'domain-import',
        component: () => import('@/views/domains/BulkImportView.vue'),
      },
      {
        path: 'domains/:uuid',
        name: 'domain-detail',
        component: () => import('@/views/domains/DomainDetailView.vue'),
        props: true,
      },
      {
        path: 'domains/groups',
        name: 'domain-groups',
        component: () => import('@/views/domains/DomainGroupsView.vue'),
      },
      {
        path: 'dns',
        name: 'dns',
        component: () => import('@/views/dns/ZoneListView.vue'),
      },
      {
        path: 'dns/zones/:id',
        name: 'dns-zone-editor',
        component: () => import('@/views/dns/ZoneEditorView.vue'),
        props: true,
      },
      // Blacklist & SSL
      {
        path: 'blacklist',
        name: 'blacklist-status',
        component: () => import('@/views/blacklist/BlacklistStatusView.vue'),
      },
      {
        path: 'blacklist/history',
        name: 'blacklist-history',
        component: () => import('@/views/blacklist/BlacklistHistoryView.vue'),
      },
      {
        path: 'blacklist/ips/add',
        name: 'blacklist-ip-add',
        component: () => import('@/views/blacklist/MonitoredIpAddView.vue'),
      },
      {
        path: 'blacklist/ips/:uuid',
        name: 'blacklist-ip-detail',
        component: () => import('@/views/blacklist/BlacklistIpDetailView.vue'),
        props: true,
      },
      {
        path: 'blacklist/:uuid',
        name: 'blacklist-domain-detail',
        component: () => import('@/views/blacklist/BlacklistDomainDetailView.vue'),
        props: true,
      },
      {
        path: 'ssl',
        name: 'ssl-status',
        component: () => import('@/views/ssl/SslStatusView.vue'),
      },
      // Billing
      {
        path: 'billing',
        name: 'billing',
        component: () => import('@/views/billing/SubscriptionView.vue'),
      },
      {
        path: 'billing/plans',
        name: 'billing-plans',
        component: () => import('@/views/billing/PricingView.vue'),
      },
      {
        path: 'billing/history',
        name: 'billing-history',
        component: () => import('@/views/billing/PaymentHistoryView.vue'),
      },
      // Settings
      {
        path: 'settings',
        redirect: { name: 'settings-profile' },
      },
      {
        path: 'settings/profile',
        name: 'settings-profile',
        component: () => import('@/views/settings/ProfileView.vue'),
      },
      {
        path: 'settings/display',
        name: 'settings-display',
        component: () => import('@/views/settings/DisplayView.vue'),
      },
      {
        path: 'settings/security',
        name: 'settings-security',
        component: () => import('@/views/settings/SecurityView.vue'),
      },
      // Notifications
      {
        path: 'notifications',
        name: 'notifications',
        component: () => import('@/views/notifications/NotificationCenterView.vue'),
      },
      {
        path: 'notifications/preferences',
        name: 'notification-preferences',
        component: () => import('@/views/notifications/PreferencesView.vue'),
      },
      // Teams
      {
        path: 'teams',
        name: 'teams',
        component: () => import('@/views/teams/TeamManagementView.vue'),
      },
      // Admin routes
      {
        path: 'admin',
        name: 'admin-dashboard',
        component: () => import('@/views/admin/AdminDashboardView.vue'),
        meta: { requiresAdmin: true },
      },
      {
        path: 'admin/users',
        name: 'admin-users',
        component: () => import('@/views/admin/AdminUsersView.vue'),
        meta: { requiresAdmin: true },
      },
      {
        path: 'admin/users/:id',
        name: 'admin-user-detail',
        component: () => import('@/views/admin/AdminUserDetailView.vue'),
        meta: { requiresAdmin: true },
        props: true,
      },
      {
        path: 'admin/accounts',
        name: 'admin-accounts',
        component: () => import('@/views/admin/AdminAccountsView.vue'),
        meta: { requiresAdmin: true },
      },
      {
        path: 'admin/accounts/:id',
        name: 'admin-account-detail',
        component: () => import('@/views/admin/AdminAccountDetailView.vue'),
        meta: { requiresAdmin: true },
        props: true,
      },
      {
        path: 'admin/settings',
        name: 'admin-settings',
        component: () => import('@/views/admin/AdminSettingsView.vue'),
        meta: { requiresAdmin: true },
      },
      {
        path: 'admin/financial',
        name: 'admin-financial',
        component: () => import('@/views/admin/AdminFinancialView.vue'),
        meta: { requiresAdmin: true },
      },
      {
        path: 'admin/discount-codes',
        name: 'admin-discount-codes',
        component: () => import('@/views/admin/AdminDiscountCodesView.vue'),
        meta: { requiresAdmin: true },
      },
      {
        path: 'admin/campaigns',
        name: 'admin-campaigns',
        component: () => import('@/views/admin/AdminCampaignsView.vue'),
        meta: { requiresAdmin: true },
      },
      {
        path: 'admin/faq',
        name: 'admin-faq',
        component: () => import('@/views/admin/AdminFaqView.vue'),
        meta: { requiresAdmin: true },
      },
    ],
  },

  // Catch-all 404
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue'),
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(_to, _from, savedPosition) {
    if (savedPosition) return savedPosition
    return { top: 0 }
  },
})

// Navigation guards
router.beforeEach(async (to, _from, next) => {
  const authStore = useAuthStore()
  const accountStore = useAccountContextStore()
  const platformStore = usePlatformStore()

  // Initialize auth state on first navigation
  if (!authStore.initialized) {
    await authStore.initialize()
  }

  if (!platformStore.loaded) {
    try {
      await platformStore.fetchStatus()
    } catch {
      // If platform status cannot be loaded, allow navigation rather than blocking the app.
    }
  }

  const isAdmin = Boolean(authStore.user?.admin_role)
  const isMaintenanceExempt =
    to.name === 'maintenance' ||
    to.name === 'login' ||
    to.name === 'two-factor' ||
    to.name === 'step-up' ||
    to.name === 'verify-email' ||
    to.name === 'verify-domain' ||
    to.name === 'verify-ip'

  if (platformStore.maintenanceMode && !isAdmin && !isMaintenanceExempt) {
    next({ name: 'maintenance' })
    return
  }

  if (to.name === 'register' && !platformStore.registrationEnabled) {
    next({ name: 'login' })
    return
  }

  const isPublic = to.matched.some((record) => record.meta.public)
  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth)
  const isGuestRoute = to.matched.some((record) => record.meta.guest)
  const requiresAdmin = to.matched.some((record) => record.meta.requiresAdmin)
  const isChallengeRoute = to.name === 'two-factor' || to.name === 'step-up'
  const isAccountPickerRoute = to.name === 'select-account'

  if (isChallengeRoute && !authStore.pendingAuth) {
    next({ name: 'login', query: to.query })
  } else if (requiresAuth && !authStore.isAuthenticated) {
    next({ name: 'login', query: { redirect: to.fullPath } })
  } else if (
    requiresAuth &&
    authStore.isAuthenticated &&
    accountStore.hasMultipleAccounts &&
    !isAccountPickerRoute &&
    to.path.startsWith('/app') &&
    !sessionStorage.getItem('account_picker_done')
  ) {
    next({ name: 'select-account', query: { redirect: to.fullPath } })
  } else if (isGuestRoute && authStore.isAuthenticated) {
    // Redirect authenticated users away from guest routes
    next({ name: 'dashboard' })
  } else if (requiresAdmin && !authStore.user?.admin_role) {
    // Redirect non-admin users to dashboard (403-like behavior)
    next({ name: 'dashboard' })
  } else if (isPublic && authStore.isAuthenticated && to.name === 'landing') {
    // Redirect authenticated users from landing to dashboard
    next({ name: 'dashboard' })
  } else {
    next()
  }
})

export default router
